import "./ApproveCandidates.css";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axiosInstance from "../axios/axiosInstance";
import { X } from "lucide-react";

function ApproveCandidateInterest() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("table");

  // For modal
  const [showModal, setShowModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [actionType, setActionType] = useState(""); // approve or reject
  const [comment, setComment] = useState("");

  // For details dialog
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedCandidateDetails, setSelectedCandidateDetails] =
    useState(null);

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const response = await axiosInstance.get(`/api/admin/admin/interests`);

      if (response.status === 200) {
        setCandidates(response.data);
      } else {
        throw new Error(
          `Failed to fetch candidates: ${response.status} - ${response.statusText}`,
        );
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
      setError(`Failed to load candidates: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal submit
  const handleAction = async (candidateParam, actionParam) => {
    // Use parameters if provided, otherwise fall back to state
    const candidate = candidateParam || selectedCandidate;
    const action = actionParam || actionType;

    if (!candidate) {
      console.error("No candidate found!");
      return;
    }
    if (!comment.trim() && action === "reject") {
      setError("Comment is required");
      return;
    }
    try {
      const response = await axiosInstance.put(
        `/api/admin/interest/${candidate.id}`,
        { action: action, admin_comment: comment },
      );
      if (response.status === 200) {
        setCandidates((prevCandidates) =>
          prevCandidates.map((c) =>
            c.id === candidate.id ? { ...c, status: response.data.status } : c,
          ),
        );
        handleCloseModal();
      } else {
        throw new Error(
          `Failed to update candidate: ${response.status} - ${response.statusText}`,
        );
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
      setError(`Failed to ${action} candidate: ${error.message}`);
    } finally {
      setShowModal(false);
    }
  };

  const openModal = (candidate, action) => {
    setSelectedCandidate(candidate);
    setActionType(action);

    setComment("");
    if (action === "reject") {
      setShowModal(true);
    } else {
      handleAction(candidate, action);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError("");
    setComment("");
    setSelectedCandidate(null);
  };
  const handleCommentChange = (e) => {
    setComment(e.target.value);
    if (e.target.value.trim() !== "") {
      setError(""); // Clear error when user starts typing
    } else {
      setError("Comment is required"); // Set error message when input is empty
    }
  };

  // Open details dialog
  const openDetailsDialog = (candidate) => {
    setSelectedCandidateDetails(candidate);
    setShowDetailsDialog(true);
  };

  // Close details dialog
  const closeDetailsDialog = () => {
    setShowDetailsDialog(false);
    setSelectedCandidateDetails(null);
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="container">
          <div className="loading-spinner">Loading candidates...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="container">
        <div className="page-header">
          <Link to="/" className="back-link" style={{ marginTop: "1rem" }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="page-title">Approve Candidate Interest</h1>
          <div className="view-toggle" role="tablist" aria-label="View mode">
            <button
              type="button"
              className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
              role="tab"
              aria-selected={viewMode === "table"}
            >
              Table
            </button>
            <button
              type="button"
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              role="tab"
              aria-selected={viewMode === "grid"}
            >
              Grid
            </button>
          </div>
        </div>

        {candidates.length === 0 ? (
          <div className="empty-state">
            <h3>No candidates found</h3>
            <p>
              There are currently no individual candidates waiting for approval.
            </p>
          </div>
        ) : (
          <>
            {viewMode === "table" ? (
              <div className="table-wrap">
                <table className="candidates-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Function</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <div className="cell-user">
                            <div className="avatar-sm">
                              <span>{c.first_name?.[0] || "U"}</span>
                            </div>
                            <div className="cell-user-meta">
                              <div className="name">
                                {c.first_name} {c.last_name}
                              </div>
                              <div className="email">{c.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>{c.job_function_name || "Not specified"}</td>
                        <td>{c.job_role_name || "Not specified"}</td>
                        <td>
                          {(() => {
                            const raw = c.status || "pending_admin";
                            const cls = raw
                              .toLowerCase()
                              .replace(/[\s_]+/g, "-")
                              .replace(/[^a-z-]/g, "");

                            const formattedStatus = raw
                              .replace(/_/g, " ")
                              .split(" ")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1),
                              )
                              .join(" ");

                            return (
                              <span className={`status-badge ${cls}`}>
                                {formattedStatus}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="decision-cell">
                          {!c.status ||
                            (c.status === "pending_admin" && (
                              <>
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => openModal(c, "approve")}
                                >
                                  Approve
                                </button>{" "}
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => openModal(c, "reject")}
                                >
                                  Reject
                                </button>
                              </>
                            ))}
                          <button
                            className="link-action"
                            onClick={() => openDetailsDialog(c)}
                            style={{ marginRight: "12px" }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="candidates-grid">
                {candidates.map((candidate) => (
                  <div key={candidate.id} className="candidate-card">
                    <div className="candidate-header">
                      <div className="candidate-avatar">
                        {candidate.profile_picture ? (
                          <img src={candidate.profile_picture} alt="Profile" />
                        ) : (
                          <span>{candidate.first_name?.charAt(0) || "U"}</span>
                        )}
                      </div>
                      <div className="candidate-info">
                        <h3>
                          {candidate.first_name} {candidate.last_name}
                        </h3>
                        <p>{candidate.email}</p>
                      </div>
                      <div
                        className={`status-badge ${(
                          candidate.status || "admin_pending"
                        )
                          .toString()
                          .toLowerCase()
                          .replace(/[\s_]+/g, "-")
                          .replace(/[^a-z-]/g, "")}`}
                      >
                        {(candidate.status || "Pending")
                          .replace(/_/g, " ")
                          .split(" ")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(" ")}
                      </div>
                    </div>

                    <div className="candidate-details">
                      <div className="detail-item">
                        <strong>Job Function Name:</strong>{" "}
                        {candidate.job_function_name || "Not specified"}
                      </div>
                      <div className="detail-item">
                        <strong>Job Role:</strong>{" "}
                        {candidate.job_role_name || "Not specified"}
                      </div>
                    </div>

                    <div className="details-item">
                      <button
                        className="link-action"
                        onClick={() => openDetailsDialog(candidate)}
                        style={{ marginRight: "8px" }}
                      >
                        View Details
                      </button>
                    </div>

                    <div className="candidate-actions">
                      {/* <button
                        className="view-details-btn"
                        onClick={() => openDetailsDialog(candidate)}
                        style={{ marginRight: "8px" }}
                      >
                        View Details
                      </button> */}
                      {(!candidate.status ||
                        candidate.status === "pending_admin") && (
                        <>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => openModal(candidate, "approve")}
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => openModal(candidate, "reject")}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Action Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: "500px" }}>
              <div className="modal-header">
                <div>
                  <h2>
                    {actionType === "approve"
                      ? "Approve Candidate"
                      : "Reject Candidate"}
                  </h2>
                  <p className="modal-subtitle">
                    {selectedCandidate?.first_name}{" "}
                    {selectedCandidate?.last_name}
                  </p>
                </div>
                <button
                  className="modal-close"
                  onClick={handleCloseModal}
                  aria-label="Close dialog"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: "12px", fontWeight: "500" }}>
                  Enter Interest Rejection Reason:
                </p>
                <textarea
                  value={comment}
                  onChange={handleCommentChange}
                  placeholder="Enter a interest rejection reason..."
                  style={{
                    width: "100%",
                    minHeight: "120px",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    fontSize: "14px",
                  }}
                />
                {error && (
                  <p
                    className="error-text"
                    style={{ color: "#ef4444", marginTop: "8px" }}
                  >
                    {error}
                  </p>
                )}
              </div>
              <div
                className="modal-footer"
                style={{
                  borderTop: "1px solid #e5e7eb",
                  padding: "20px 24px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <button
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => handleAction()}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Details Dialog */}
        {showDetailsDialog && selectedCandidateDetails?.interest_context && (
          <div className="dialog-overlay" onClick={closeDetailsDialog}>
            <div
              className="dialog-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="dialog-header">
                <h2>Company Details</h2>
                <button className="modal-close" onClick={closeDetailsDialog}>
                  <X size={24} />
                </button>
              </div>

              <div className="dialog-body">
                <div className="candidate-details-content">
                  {selectedCandidateDetails.interest_context.corporate && (
                    <div className="detail-section">
                      <h3>Corporate Information</h3>
                      <div className="detail-row">
                        <strong>Company Name:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.corporate
                            .name || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Website:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.corporate
                            .url ? (
                            <a
                              href={
                                selectedCandidateDetails.interest_context
                                  .corporate.url
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {
                                selectedCandidateDetails.interest_context
                                  .corporate.url
                              }
                            </a>
                          ) : (
                            "Not specified"
                          )}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Industry:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.corporate
                            .industry || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Company Size:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.corporate
                            .size || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Company Type:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.corporate
                            .company_type || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Business Email:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.corporate
                            .business_email || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Phone Number:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.corporate
                            .phone_num || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Email Domain:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.corporate
                            .email_domain || "Not specified"}
                        </span>
                      </div>
                      {selectedCandidateDetails.interest_context.corporate
                        .description && (
                        <div className="detail-row">
                          <strong>Description:</strong>
                          <span>
                            {
                              selectedCandidateDetails.interest_context
                                .corporate.description
                            }
                          </span>
                        </div>
                      )}
                      {/* {selectedCandidateDetails.interest_context.corporate
                      .company_value && (
                      <div className="detail-row">
                        <strong>Company Value:</strong>
                        <span>
                          {
                            selectedCandidateDetails.interest_context.corporate
                              .company_value
                          }
                        </span>
                      </div>
                    )} */}
                      {/* {selectedCandidateDetails.interest_context.corporate
                      .culture_values &&
                      selectedCandidateDetails.interest_context.corporate
                        .culture_values.length > 0 && (
                        <div className="detail-row">
                          <strong>Culture Values:</strong>
                          <span>
                            {selectedCandidateDetails.interest_context.corporate.culture_values.join(
                              ", "
                            )}
                          </span>
                        </div>
                      )} */}
                      {selectedCandidateDetails.interest_context.corporate
                        .work_environment &&
                        selectedCandidateDetails.interest_context.corporate
                          .work_environment.length > 0 && (
                          <div className="detail-row">
                            <strong>Work Environment:</strong>
                            <span>
                              {selectedCandidateDetails.interest_context.corporate.work_environment.join(
                                ", ",
                              )}
                            </span>
                          </div>
                        )}
                      {selectedCandidateDetails.interest_context.corporate
                        .contact_person_name && (
                        <div className="detail-row">
                          <strong>Contact Person Name:</strong>
                          <span>
                            {
                              selectedCandidateDetails.interest_context
                                .corporate.contact_person_name
                            }
                          </span>
                        </div>
                      )}
                      {selectedCandidateDetails.interest_context.corporate
                        .contact_person_email && (
                        <div className="detail-row">
                          <strong>Contact Person Email:</strong>
                          <span>
                            {
                              selectedCandidateDetails.interest_context
                                .corporate.contact_person_email
                            }
                          </span>
                        </div>
                      )}
                      {selectedCandidateDetails.interest_context.corporate
                        .contact_person_phone && (
                        <div className="detail-row">
                          <strong>Contact Person Phone:</strong>
                          <span>
                            {
                              selectedCandidateDetails.interest_context
                                .corporate.contact_person_phone
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedCandidateDetails.interest_context.job_posting && (
                    <div className="detail-section">
                      <h3>Job Posting Information</h3>
                      <div className="detail-row">
                        <strong>Role Name:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.job_posting
                            .role_name || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Function Name:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.job_posting
                            .function_name || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Country:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.job_posting
                            .country || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Experience Level:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.job_posting
                            .experience_level || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Summary:</strong>
                        <span>
                          {selectedCandidateDetails.interest_context.job_posting
                            .summary || "Not specified"}
                        </span>
                      </div>
                      {selectedCandidateDetails.interest_context.job_posting
                        .why_join_team && (
                        <div className="detail-row">
                          <strong>Why Join Team:</strong>
                          <span>
                            {
                              selectedCandidateDetails.interest_context
                                .job_posting.why_join_team
                            }
                          </span>
                        </div>
                      )}
                      {selectedCandidateDetails.interest_context.job_posting
                        .role_open_reason && (
                        <div className="detail-row">
                          <strong>Role Open Reason:</strong>
                          <span>
                            {
                              selectedCandidateDetails.interest_context
                                .job_posting.role_open_reason
                            }
                          </span>
                        </div>
                      )}
                      {selectedCandidateDetails.interest_context.job_posting
                        .company_type &&
                        selectedCandidateDetails.interest_context.job_posting
                          .company_type.length > 0 && (
                          <div className="detail-row">
                            <strong>Company Type:</strong>
                            <span>
                              {selectedCandidateDetails.interest_context.job_posting.company_type.join(
                                ", ",
                              )}
                            </span>
                          </div>
                        )}
                      {selectedCandidateDetails.interest_context.job_posting
                        .employment_type &&
                        selectedCandidateDetails.interest_context.job_posting
                          .employment_type.length > 0 && (
                          <div className="detail-row">
                            <strong>Employment Type:</strong>
                            <span>
                              {selectedCandidateDetails.interest_context.job_posting.employment_type.join(
                                ", ",
                              )}
                            </span>
                          </div>
                        )}
                      {selectedCandidateDetails.interest_context.job_posting
                        .work_environment &&
                        selectedCandidateDetails.interest_context.job_posting
                          .work_environment.length > 0 && (
                          <div className="detail-row">
                            <strong>Work Environment:</strong>
                            <span>
                              {selectedCandidateDetails.interest_context.job_posting.work_environment.join(
                                ", ",
                              )}
                            </span>
                          </div>
                        )}
                      {selectedCandidateDetails.interest_context.job_posting
                        .selected_soft_skills &&
                        selectedCandidateDetails.interest_context.job_posting
                          .selected_soft_skills.length > 0 && (
                          <div className="detail-row">
                            <strong>Soft Skills:</strong>
                            <span>
                              {selectedCandidateDetails.interest_context.job_posting.selected_soft_skills.join(
                                ", ",
                              )}
                            </span>
                          </div>
                        )}
                    </div>
                  )}

                  {selectedCandidateDetails.interest_context
                    .job_posting_skills && (
                    <div className="detail-section">
                      <h3>Job Posting Skills</h3>
                      {selectedCandidateDetails.interest_context
                        .job_posting_skills.selected_must_have_skills &&
                        selectedCandidateDetails.interest_context
                          .job_posting_skills.selected_must_have_skills.length >
                          0 && (
                          <div style={{ marginBottom: "20px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "8px" }}
                            >
                              Must Have Skills:
                            </strong>
                            {selectedCandidateDetails.interest_context.job_posting_skills.selected_must_have_skills.map(
                              (skillObj, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    marginBottom: "12px",
                                    padding: "12px",
                                    background: "#f8f9fa",
                                    borderRadius: "6px",
                                    border: "1px solid #e9ecef",
                                  }}
                                >
                                  {Object.entries(skillObj).map(
                                    ([skillName, skillData]) => (
                                      <div key={skillName}>
                                        <strong>{skillName}</strong>
                                        {(!skillData.subSkill ||
                                          skillData.subSkill.length === 0) && (
                                          <> - Level: {skillData.level}</>
                                        )}
                                        {skillData.subSkill &&
                                          skillData.subSkill.length > 0 && (
                                            <div
                                              style={{
                                                marginTop: "6px",
                                                marginLeft: "16px",
                                                fontSize: "13px",
                                                color: "#666",
                                              }}
                                            >
                                              Sub-skills:{" "}
                                              {skillData.subSkill
                                                .map((sub) =>
                                                  typeof sub === "string"
                                                    ? sub
                                                    : `${sub.name} (${sub.level})`,
                                                )
                                                .join(", ")}
                                            </div>
                                          )}
                                      </div>
                                    ),
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      {selectedCandidateDetails.interest_context
                        .job_posting_skills.selected_should_have_skills &&
                        selectedCandidateDetails.interest_context
                          .job_posting_skills.selected_should_have_skills
                          .length > 0 && (
                          <div style={{ marginBottom: "20px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "8px" }}
                            >
                              Should Have Skills:
                            </strong>
                            {selectedCandidateDetails.interest_context.job_posting_skills.selected_should_have_skills.map(
                              (skillObj, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    marginBottom: "12px",
                                    padding: "12px",
                                    background: "#f8f9fa",
                                    borderRadius: "6px",
                                    border: "1px solid #e9ecef",
                                  }}
                                >
                                  {Object.entries(skillObj).map(
                                    ([skillName, skillData]) => (
                                      <div key={skillName}>
                                        <strong>{skillName}</strong>
                                        {(!skillData.subSkill ||
                                          skillData.subSkill.length === 0) && (
                                          <> - Level: {skillData.level}</>
                                        )}
                                        {skillData.subSkill &&
                                          skillData.subSkill.length > 0 && (
                                            <div
                                              style={{
                                                marginTop: "6px",
                                                marginLeft: "16px",
                                                fontSize: "13px",
                                                color: "#666",
                                              }}
                                            >
                                              Sub-skills:{" "}
                                              {skillData.subSkill
                                                .map((sub) =>
                                                  typeof sub === "string"
                                                    ? sub
                                                    : `${sub.name} (${sub.level})`,
                                                )
                                                .join(", ")}
                                            </div>
                                          )}
                                      </div>
                                    ),
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      {selectedCandidateDetails.interest_context
                        .job_posting_skills.selected_could_have_skills &&
                        selectedCandidateDetails.interest_context
                          .job_posting_skills.selected_could_have_skills
                          .length > 0 && (
                          <div style={{ marginBottom: "20px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "8px" }}
                            >
                              Could Have Skills:
                            </strong>
                            {selectedCandidateDetails.interest_context.job_posting_skills.selected_could_have_skills.map(
                              (skillObj, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    marginBottom: "12px",
                                    padding: "12px",
                                    background: "#f8f9fa",
                                    borderRadius: "6px",
                                    border: "1px solid #e9ecef",
                                  }}
                                >
                                  {Object.entries(skillObj).map(
                                    ([skillName, skillData]) => (
                                      <div key={skillName}>
                                        <strong>{skillName}</strong>
                                        {(!skillData.subSkill ||
                                          skillData.subSkill.length === 0) && (
                                          <> - Level: {skillData.level}</>
                                        )}
                                        {skillData.subSkill &&
                                          skillData.subSkill.length > 0 && (
                                            <div
                                              style={{
                                                marginTop: "6px",
                                                marginLeft: "16px",
                                                fontSize: "13px",
                                                color: "#666",
                                              }}
                                            >
                                              Sub-skills:{" "}
                                              {skillData.subSkill
                                                .map((sub) =>
                                                  typeof sub === "string"
                                                    ? sub
                                                    : `${sub.name} (${sub.level})`,
                                                )
                                                .join(", ")}
                                            </div>
                                          )}
                                      </div>
                                    ),
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ApproveCandidateInterest;
